import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api-client';
import { canSearchPatientLookup, canSearchPatientUhid } from '../../lib/patient-uhid-search';
import {
  addOpdRegistrationPayment,
  addOpdRegistrationRefund,
  cancelOpdAppointment,
  cancelOpdRegistrationBill,
  createOpdAppointment,
  createOpdRegistration,
  fetchOpdAppointment,
  fetchOpdAppointments,
  fetchOpdLookups,
  fetchOpdPrescription,
  fetchOpdPrescriptions,
  fetchOpdRegistration,
  fetchOpdRegistrations,
  fetchOpdSchedules,
  generateOpdSchedules,
  removeOpdSchedules,
  rescheduleOpdAppointment,
  saveOpdPrescription,
  updateOpdRegistration,
  updateOpdRegistrationPayment,
  updateOpdSchedule,
  updateOpdScheduleStatus,
} from './opd.api';
import type { OpdListFilters, OpdPayment, UpdateRegistrationPayload, UpsertOpdPrescriptionPayload } from './types';

export const useOpdRegistrations = (filters: OpdListFilters, enabled = true) => useQuery({ queryKey: ['opd', 'registrations', filters], queryFn: () => fetchOpdRegistrations(filters), enabled });
export const useOpdPatientBills = (uhid: string | null) => useOpdRegistrations({ page: 1, limit: 200, field: 'uhid', value: uhid ?? '' }, Boolean(uhid));
export const useOpdRegistration = (id?: string) => useQuery({ queryKey: ['opd', 'registration', id], queryFn: () => fetchOpdRegistration(id!), enabled: Boolean(id) });
export const useOpdPrescriptions = (filters: OpdListFilters, enabled = true) => useQuery({ queryKey: ['opd', 'prescriptions', filters], queryFn: () => fetchOpdPrescriptions(filters), enabled });
export const useOpdPatientPrescriptions = (uhid: string | null) => useOpdPrescriptions({ page: 1, limit: 200, field: 'uhid', value: uhid ?? '' }, Boolean(uhid));
export const useOpdPrescription = (registrationId?: string, enabled = true) => useQuery({
  queryKey: ['opd', 'prescription', registrationId],
  queryFn: () => fetchOpdPrescription(registrationId!),
  enabled: Boolean(registrationId) && enabled,
  retry: (failureCount, error) => error instanceof ApiError && error.status === 404 ? false : failureCount < 2,
});
export const useOpdAppointments = (filters: OpdListFilters) => useQuery({ queryKey: ['opd', 'appointments', filters], queryFn: () => fetchOpdAppointments(filters) });
export const useOpdAppointment = (id?: string) => useQuery({ queryKey: ['opd', 'appointment', id], queryFn: () => fetchOpdAppointment(id!), enabled: Boolean(id) });
export const useOpdLookup = (type: string, search = '', departmentId = '', state = '') => {
  const patientLookup = type === 'patients' || type.startsWith('appointment-patients');
  const alwaysFresh = patientLookup || type === 'doctors';
  return useQuery({
    queryKey: ['opd', 'lookup', type, search, departmentId, state],
    queryFn: () => fetchOpdLookups(type, search, departmentId, state),
    enabled: !patientLookup || (type === 'patients' ? canSearchPatientUhid(search) : canSearchPatientLookup(search)),
    ...(alwaysFresh ? { staleTime: 0, refetchOnMount: 'always' as const } : { staleTime: 60_000 }),
  });
};
export const useOpdSchedules = (doctorId: string, fromDate = '', toDate = '', activeOnly = false) => useQuery({ queryKey: ['opd', 'schedules', doctorId, fromDate, toDate, activeOnly], queryFn: () => fetchOpdSchedules(doctorId, fromDate, toDate, activeOnly), enabled: Boolean(doctorId) });

export const useCreateOpdRegistration = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: createOpdRegistration, onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }) });
};
export const useUpdateOpdRegistration = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRegistrationPayload }) => updateOpdRegistration(id, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }),
  });
};
export const useUpdateOpdRegistrationPayment = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentIndex, payload }: { id: string; paymentIndex: number; payload: { mode: OpdPayment['mode']; bankName: string | null; amount: number } }) =>
      updateOpdRegistrationPayment(id, paymentIndex, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }),
  });
};
export const useAddOpdRegistrationPayment = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { mode: OpdPayment['mode']; bankName: string | null; amount: number } }) =>
      addOpdRegistrationPayment(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['opd'] });
      void client.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};
export const useAddOpdRegistrationRefund = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { mode: OpdPayment['mode']; bankName: string | null; amount: number } }) =>
      addOpdRegistrationRefund(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['opd'] });
      void client.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};
export const useSaveOpdPrescription = (registrationId: string, mode: 'create' | 'edit') => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpsertOpdPrescriptionPayload) => saveOpdPrescription(registrationId, payload, mode),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }),
  });
};
export const useCancelOpdRegistrationBill = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelOpdRegistrationBill(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['opd'] });
      void client.invalidateQueries({ queryKey: ['patients'] });
    },
  });
};
export const useCreateOpdAppointment = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: createOpdAppointment, onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }) });
};
export const useRescheduleOpdAppointment = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: ({ id, slot }: { id: string; slot: string }) => rescheduleOpdAppointment(id, slot), onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }) });
};
export const useCancelOpdAppointment = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: cancelOpdAppointment, onSuccess: () => void client.invalidateQueries({ queryKey: ['opd'] }) });
};
export const useGenerateOpdSchedules = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: generateOpdSchedules, onSuccess: () => void client.invalidateQueries({ queryKey: ['opd', 'schedules'] }) });
};
export const useUpdateOpdSchedule = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: ({ id, payload }: { id: string; payload: { fromTime: string; toTime: string; patientPerSlot: number } }) => updateOpdSchedule(id, payload), onSuccess: () => void client.invalidateQueries({ queryKey: ['opd', 'schedules'] }) });
};
export const useUpdateOpdScheduleStatus = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: ({ ids, status }: { ids: string[]; status: 'active' | 'inactive' }) => updateOpdScheduleStatus(ids, status), onSuccess: () => void client.invalidateQueries({ queryKey: ['opd', 'schedules'] }) });
};
export const useRemoveOpdSchedules = () => {
  const client = useQueryClient();
  return useMutation({ mutationFn: removeOpdSchedules, onSuccess: () => void client.invalidateQueries({ queryKey: ['opd', 'schedules'] }) });
};
